import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetMaterialTypes } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import { IMaterialType, MaterialType } from '../../../@types/model/masterData/materialType/materialType';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import MaterialTypeHttpService from '../../../services/http/masterData/materialTypeHttpService';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { MaterialTypeFormValues, IMaterialTypeFormValues } from '../../../@types/model/masterData/materialType/materialTypeFormValues';
import MaterialTypeForm from './form/MaterialTypeForm';
import { useLocation } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { setMaterialTypesMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { IApiError } from '../../../@types/other';

const MaterialTypeScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const materialTypes = useAppSelector(x => x.masterData.materialTypes);
    const auth = useAppSelector(x => x.auth);
    const pathname = useLocation()?.pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedMaterialType, setSelectedMaterialType] = useState<IMaterialType>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingMaterialType, setDeletingMaterialType] = useState<IMaterialType>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await MaterialTypeHttpService.getMaterialTypeData();
                    dispatch(dataSetMaterialTypes(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading material types.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = useCallback(() => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setFormGuid(undefined);
        setSelectedMaterialType(undefined);
    }, []);

    const onSubmit = async (values : IMaterialTypeFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        if (isNullOrWhiteSpace(payload.guid)) {
            payload.guid = formGuid ?? '';
        }

        try {
            const res = await MaterialTypeHttpService.addOrUpdateMaterialType(MaterialType(payload));
            const updatedList = upsertArrayElement(materialTypes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetMaterialTypes(updatedList));
            await setMaterialTypesMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedMaterialType ? 'Material Type updated successfully.' : 'Material Type added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating material type data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = useCallback((materialType : IMaterialType) => {
        setDeletingMaterialType(materialType);
        setIsDeletePopupOpen(true);
    }, []);

    const closeDeleteConfirmationPopup = useCallback(() => {
        setDeletingMaterialType(undefined);
        setIsDeletePopupOpen(false);
    }, []);

    const removeMaterialType = async () => {
        if (!deletingMaterialType) return;
        const updatedMaterialType = { ...deletingMaterialType, isActive: false };
        setIsLoading(true);

        try {
            const res = await MaterialTypeHttpService.deleteMaterialType(updatedMaterialType.id);
            const updatedList = upsertArrayElement(materialTypes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetMaterialTypes(updatedList));
            await setMaterialTypesMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Material Type updated successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting material type.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const editMaterialType = useCallback((materialType : IMaterialType) => {
        setSelectedMaterialType(materialType);
        setIsDialogOpen(true);
    }, []);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => pathname?.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return MaterialTypeFormValues(selectedMaterialType);
        }
    }, [selectedMaterialType, isDialogOpen]);

    return (
        <Screen isLoading={isLoading}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Material Type'
                    isEdit={!!selectedMaterialType}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <MaterialTypeForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IMaterialType>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editMaterialType}
                        enableEditing={hasEditingRight}
                        enableDeleting={materialType => materialType.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        fitWidthToPage
                        enablePagination
                        columns={[
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Pack Type?', field: 'isPackType', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={materialTypes}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Material Type'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this material type?'
                onAccept={removeMaterialType}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default MaterialTypeScreen;
